import axios from 'axios';
import config from '@/config/env';

export const api = axios.create({
  baseURL: `${config.API_BASE_URL}/api`,
  withCredentials: true,   // ← ALWAYS send cookies
  headers: {
    'X-Auth-Scope': 'admin',
  },
});

// Track if we're already refreshing to prevent multiple refresh attempts
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (error?: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  
  failedQueue = [];
};

// Global 401 interceptor → silent refresh
api.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config;
    const requestUrl = String(originalRequest?.url || '');
    const isRefreshCall = requestUrl.includes('/admins/refresh-token');

    // Never try to refresh the refresh call itself — that loops into forced logout.
    if (error.response?.status === 401 && isRefreshCall) {
      localStorage.removeItem('user');
      sessionStorage.clear();
      if (typeof window !== 'undefined' && !window.location.pathname.includes('/login')) {
        window.location.href = '/login';
      }
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      
      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(() => {
          return api(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Attempt to refresh the token
        const refreshResponse = await api.post('/admins/refresh-token');

        // NestJS @Post defaults to HTTP 201; treat any 2xx as success.
        if (refreshResponse.status >= 200 && refreshResponse.status < 300) {
          processQueue(null, 'refreshed');

          // Retry the original request
          return api(originalRequest);
        } else {
          throw new Error('Refresh failed');
        }
      } catch (refreshError: any) {
        processQueue(refreshError, null);
        
        // Clear any stored auth state
        localStorage.removeItem('user');
        sessionStorage.clear();
        
        // Redirect to login page
        if (typeof window !== 'undefined' && !window.location.pathname.includes('/login')) {
          window.location.href = '/login';
        }
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
    
    return Promise.reject(error);
  },
);

export default api;
